import React, { useState, useEffect, useRef } from 'react';
import { Send, Loader, FileText, MessageSquare } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { sendChatMessage } from '../services/api';

const ChatInterface = ({ sessionId, currentPage, isDirect = false }) => {
  const initialMessages = isDirect
    ? [
        {
          id: 1,
          content: "Hello! I'm your Medical Compliance Assistant. How can I help you today? Feel free to ask me any medical or compliance related questions.",
          role: 'bot',
        },
      ]
    : [
        {
          id: 1,
          content: "Hello! I'm your Medical Compliance Assistant. I can help you understand your document and answer any questions you have about it. I have access to the entire document, so feel free to ask about any part. What would you like to know?",
          role: 'bot',
        },
      ];

  const [messages, setMessages] = useState(initialMessages);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Adjust textarea height based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        150
      )}px`;
    }
  }, [newMessage]);

  // Scroll chat to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle message submission
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) return;
    
    // Add user message
    const userMessage = {
      id: Date.now(),
      content: newMessage,
      role: 'user',
    };
    
    setMessages([...messages, userMessage]);
    setNewMessage('');
    setIsLoading(true);
    
    try {
      // Log environment variables for debugging
      console.log("USE_BACKEND env var:", process.env.REACT_APP_USE_BACKEND);
      console.log("API_URL env var:", process.env.REACT_APP_API_URL);
      console.log("Session ID:", sessionId);
      console.log("Is Direct Chat:", isDirect);
      
      // Force using the backend regardless of environment variables for testing
      // Always try to use the backend if we have a session ID
      const useBackend = true && !!sessionId;
      
      // If we have backend API available and a session ID
      if (useBackend && sessionId) {
        try {
          console.log("Sending to backend:", sessionId, newMessage);
          const response = await sendChatMessage(sessionId, newMessage, currentPage, !isDirect);
          console.log("Backend response:", response);
          
          const botResponse = {
            id: Date.now() + 1,
            content: response.message || "I'm sorry, I couldn't process that request.",
            role: 'bot',
          };
          
          setMessages((prevMessages) => [...prevMessages, botResponse]);
          setIsLoading(false);
        } catch (error) {
          console.error("Backend chat error:", error);
          // Fall back to simulated response
          setTimeout(() => {
            const botResponse = {
              id: Date.now() + 1,
              content: isDirect
                ? getDirectChatResponse(newMessage)
                : "Sorry, I had trouble connecting to the AI backend. Here's a simulated response: " + getSimulatedResponse(newMessage, currentPage),
              role: 'bot',
            };
            
            setMessages((prevMessages) => [...prevMessages, botResponse]);
            setIsLoading(false);
          }, 1000);
        }
      } else {
        // Fallback to simulated responses for demo purposes
        setTimeout(() => {
          const botResponse = {
            id: Date.now() + 1,
            content: isDirect
              ? getDirectChatResponse(newMessage)
              : getSimulatedResponse(newMessage, currentPage),
            role: 'bot',
          };
          
          setMessages((prevMessages) => [...prevMessages, botResponse]);
          setIsLoading(false);
        }, 1000);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Add error message
      const errorMessage = {
        id: Date.now() + 1,
        content: "I'm sorry, there was an error processing your request. Please try again.",
        role: 'bot',
        isError: true,
      };
      
      setMessages((prevMessages) => [...prevMessages, errorMessage]);
      setIsLoading(false);
    }
  };

  // Generate simulated responses for direct chat (no PDF)
  const getDirectChatResponse = (message) => {
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey')) {
      return `Hello! I'm your Medical Compliance Assistant. I can help with various medical and compliance related topics. What would you like to know about today?`;
    }
    
    if (lowerMessage.includes('what can you do') || lowerMessage.includes('capabilities') || lowerMessage.includes('help me with')) {
      return `I can help you with various medical and compliance related topics including:

1. Medical terminology and explanations
2. Compliance requirements for medical documentation
3. Regulatory guidelines in healthcare
4. Best practices for medical professionals
5. General health information

You can also upload a PDF document for detailed analysis and compliance checking.`;
    }

    if (lowerMessage.includes('upload') || lowerMessage.includes('pdf') || lowerMessage.includes('document')) {
      return `To analyze a specific PDF document, you'll need to return to the home page and use the "Upload PDF" option. This will allow me to perform detailed analysis on your document, including compliance checks and provide page-specific insights.`;
    }
    
    if (lowerMessage.includes('compliance') || lowerMessage.includes('regulation')) {
      return `Medical compliance refers to adhering to laws, regulations, guidelines, and specifications relevant to medical practices. Some key aspects include:

1. HIPAA compliance for patient data privacy
2. FDA regulations for medical products
3. Clinical trial compliance
4. Documentation requirements
5. Safety protocols and reporting

Is there a specific area of compliance you're interested in?`;
    }
    
    // Default response for direct chat
    return `Thank you for your question. As a Medical Compliance Assistant, I can provide general information about medical topics and compliance requirements. For more specific document analysis, you can upload a PDF through the home page option.

To better assist you with your current query, could you provide more details or specify what particular aspect you're interested in?`;
  };

  // Generate simulated responses for demo
  const getSimulatedResponse = (message, currentPageNum) => {
    const lowerMessage = message.toLowerCase();
    
    // Responses for full document queries
    if (lowerMessage.includes('full document') || lowerMessage.includes('entire document') || 
        lowerMessage.includes('whole document') || lowerMessage.includes('all pages') ||
        lowerMessage.includes('summary of the full') || lowerMessage.includes('summary of the entire')) {
      return `I've analyzed the complete document. This appears to be a medical regulatory document concerning ranolazine, a medication for cardiac conditions. The document contains information about dosage forms (375mg, 500mg, 750mg, and 1000mg), safety guidelines, and compliance requirements. Throughout the document, there are several references to clinical studies and regulatory frameworks. The document is structured with clear sections covering indications, contraindications, dosing information, and safety monitoring protocols.`;
    }
    
    if (lowerMessage.includes('citation') || lowerMessage.includes('reference') || lowerMessage.includes('bibliography')) {
      if (lowerMessage.includes('all') || lowerMessage.includes('document')) {
        return `I found 12 citations throughout the document. The main references include Johnson et al. (2020), European Medical Agency Guidelines (2023), Internal Protocol IP-2023-01, and several clinical studies published between 2018-2023. Would you like me to list all of them or focus on a specific section?`;
      } else {
        return `I found 3 citations on the current page. They reference Johnson et al. (2020), European Medical Agency Guidelines (2023), and Internal Protocol IP-2023-01.`;
      }
    }
    
    if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey')) {
      return `Hello! I'm here to help you analyze this document. I have access to all pages and can answer questions about any part of the document or provide a comprehensive analysis. What would you like to know?`;
    }
    
    if (lowerMessage.includes('compliance') || lowerMessage.includes('regulation')) {
      if (lowerMessage.includes('all') || lowerMessage.includes('document') || lowerMessage.includes('entire')) {
        return `Overall, this document follows compliance guidelines for medical documentation. Based on my analysis of all 82 pages, the content appears to be fully compliant with current EU and international regulatory standards for medical documentation. There are a few minor formatting inconsistencies on pages 24, 37, and 61 that could be addressed, but they don't affect regulatory compliance.`;
      } else {
        return `The content on page ${currentPageNum} appears to be compliant with guidelines for medical documentation. All required elements are present and properly formatted according to current regulatory standards. Would you like me to check for specific compliance issues?`;
      }
    }
    
    if (lowerMessage.includes('ranolazine') || lowerMessage.includes('medication')) {
      return `Ranolazine is discussed throughout the document as a medication for cardiac conditions. It's available in multiple dosage forms (375mg, 500mg, 750mg, and 1000mg tablets). The document outlines its indication for treating stable angina pectoris, contraindications, dosing guidelines, and safety monitoring requirements. Clinical studies referenced in the document indicate efficacy in reducing angina frequency when used as prescribed.`;
    }
    
    if (lowerMessage.includes('page') && /\d+/.test(lowerMessage)) {
      // Extract page number from message
      const pageMatch = lowerMessage.match(/page\s+(\d+)/i);
      const pageNum = pageMatch ? parseInt(pageMatch[1]) : null;
      
      if (pageNum && pageNum <= 82 && pageNum >= 1) {
        return `I've examined page ${pageNum}. This page contains information about ${getRandomPageContent(pageNum)}. It's properly formatted and follows the document's overall structure. Would you like me to explain any specific aspect of this page?`;
      }
    }
    
    // Default response including awareness of current page
    return `I've analyzed your document and can see you're currently viewing page ${currentPageNum}. This page contains information about ranolazine dosage forms. Remember, I have access to the entire 82-page document, so feel free to ask me about any section or page, or request a comprehensive analysis of the whole document.`;
  };

  // Helper function to generate random page content for demo
  const getRandomPageContent = (pageNum) => {
    const contents = [
      "dosage recommendations for ranolazine",
      "clinical study results for cardiac patients",
      "safety monitoring protocols",
      "contraindications and drug interactions",
      "regulatory compliance requirements",
      "patient information guidelines",
      "pharmacokinetic properties of ranolazine",
      "administrative documentation procedures"
    ];
    
    // Use page number to consistently return the same content for the same page
    return contents[pageNum % contents.length];
  };

  // Handle text input change
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    // Send message on Enter without Shift
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  return (
    <div className="flex flex-col flex-grow h-full">
      {/* Messages area - fixed height with scrolling */}
      <div className="flex-grow p-4 overflow-y-auto bg-dark-darker h-[calc(100vh-185px)] max-h-[calc(100vh-185px)]">
        <div className="space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`chat-message ${
                message.role === 'user' ? 'user-message' : 'bot-message'
              } ${message.isError ? 'border border-red-500' : ''}`}
            >
              <div className="whitespace-pre-wrap">
                <ReactMarkdown>
                  {message.content}
                </ReactMarkdown>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="chat-message bot-message">
              <div className="flex items-center">
                <Loader size={16} className="animate-spin mr-2" />
                <span>Thinking...</span>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>
      
      {/* Input area */}
      <form onSubmit={handleSendMessage} className="p-3 border-t border-dark bg-dark">
        <div className="flex flex-col">
          <div className="mb-2 text-xs text-primary-light flex items-center">
            {isDirect ? (
              <>
                <MessageSquare size={12} className="mr-1" />
                <span>Direct chat mode</span>
              </>
            ) : (
              <>
                <FileText size={12} className="mr-1" />
                <span>AI assistant has access to your entire document</span>
              </>
            )}
          </div>
          <div className="flex items-end">
            <textarea
              ref={textareaRef}
              value={newMessage}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              className="flex-grow bg-dark-lighter text-white p-3 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              rows={1}
            />
            <button
              type="submit"
              className={`ml-2 p-3 rounded-lg transition duration-200 ${
                newMessage.trim()
                  ? 'bg-primary hover:bg-primary-dark text-white'
                  : 'bg-dark-lighter text-gray-500 cursor-not-allowed'
              }`}
              disabled={!newMessage.trim()}
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ChatInterface;